import type {
  AccessArea,
  AccessControlCatalog,
  AccessRole,
  AccessRule,
  CardAccessProfile,
  ZoneTemplate,
} from "../domain/accessControl.ts";

const ACCESS_AREAS: AccessArea[] = [
  { id: "area-room", code: "room", name: "Room", category: "guest", description: "Assigned guest room door." },
  { id: "area-elevator", code: "elevator", name: "Elevator", category: "transport", description: "Guest and service elevator access." },
  { id: "area-gym", code: "gym", name: "Gym", category: "amenity", description: "Fitness and wellness area." },
  { id: "area-spa", code: "spa", name: "Spa", category: "amenity", description: "Spa and thermal zone." },
  { id: "area-vip-lounge", code: "vip_lounge", name: "VIP Lounge", category: "amenity", description: "Private lounge for VIP access." },
  { id: "area-staff-only", code: "staff_only", name: "Staff Only", category: "staff", description: "Restricted staff circulation." },
  { id: "area-technical", code: "technical", name: "Technical", category: "technical", description: "Plant rooms and engineering points." },
  { id: "area-parking", code: "parking", name: "Parking", category: "parking", description: "Guest and staff parking gates." },
  { id: "area-conference", code: "conference", name: "Conference", category: "event", description: "Conference and meeting rooms." },
  { id: "area-service-room", code: "service_room", name: "Service Rooms", category: "service", description: "Housekeeping and service storage rooms." },
];

const ACCESS_ROLES: AccessRole[] = [
  { id: "role-guest", code: "guest", name: "Guest", description: "Standard guest access.", holderType: "guest", status: "active" },
  { id: "role-vip-guest", code: "vip_guest", name: "VIP Guest", description: "Premium amenity access.", holderType: "guest", status: "active" },
  { id: "role-housekeeping", code: "housekeeping", name: "Housekeeping", description: "Shift-limited guest floor access.", holderType: "staff", status: "active" },
  { id: "role-security", code: "security", name: "Security", description: "24/7 perimeter and response access.", holderType: "staff", privileged: true, status: "active" },
  { id: "role-maintenance", code: "maintenance", name: "Maintenance", description: "Service and engineering access.", holderType: "staff", status: "active" },
  { id: "role-manager", code: "manager", name: "Manager", description: "Operational oversight access.", holderType: "staff", privileged: true, status: "active" },
  { id: "role-front-office-supervisor", code: "front_office_supervisor", name: "Front Office Supervisor", description: "Front office and supervisory circulation access.", holderType: "staff", status: "active" },
  { id: "role-it-admin", code: "it_admin", name: "IT Admin", description: "Technical and backend access for system administration.", holderType: "staff", privileged: true, status: "active" },
];

const ZONE_TEMPLATES: ZoneTemplate[] = [
  {
    id: "template-guest-standard",
    code: "guest_standard",
    name: "Guest Standard",
    description: "Core guest circulation and assigned room.",
    areaIds: ["area-room", "area-elevator", "area-parking"],
    status: "active",
  },
  {
    id: "template-vip-access",
    code: "vip_access",
    name: "VIP Access",
    description: "Premium guest areas and lounge privileges.",
    areaIds: ["area-room", "area-elevator", "area-spa", "area-vip-lounge", "area-parking"],
    status: "active",
  },
  {
    id: "template-housekeeping-shift",
    code: "housekeeping_shift",
    name: "Housekeeping Shift",
    description: "Guest rooms, service areas and staff circulation during shift.",
    areaIds: ["area-room", "area-elevator", "area-staff-only", "area-service-room"],
    status: "active",
  },
  {
    id: "template-maintenance-shift",
    code: "maintenance_shift",
    name: "Maintenance Shift",
    description: "Engineering, service rooms and elevator access.",
    areaIds: ["area-technical", "area-staff-only", "area-service-room", "area-elevator"],
    status: "active",
  },
  {
    id: "template-security-master",
    code: "security_master",
    name: "Security Master",
    description: "Broad operational and perimeter access.",
    areaIds: ACCESS_AREAS.map((area) => area.id),
    status: "active",
  },
  {
    id: "template-supervisor-ops",
    code: "supervisor_ops",
    name: "Supervisor Operations",
    description: "Front office, conference and staff circulation.",
    areaIds: ["area-elevator", "area-staff-only", "area-conference", "area-parking"],
    status: "active",
  },
  {
    id: "template-it-admin",
    code: "it_admin",
    name: "IT Admin",
    description: "Technical, service and staff-only access for system administrators.",
    areaIds: ["area-technical", "area-staff-only", "area-service-room", "area-elevator", "area-parking"],
    status: "active",
  },
  {
    id: "template-conference-guest",
    code: "conference_guest",
    name: "Conference Guest",
    description: "Temporary conference and parking access.",
    areaIds: ["area-conference", "area-elevator", "area-parking"],
    status: "active",
  },
];

const ACCESS_RULES: AccessRule[] = [
  {
    id: "rule-guest-default",
    roleId: "role-guest",
    areaIds: ["area-gym"],
    zoneTemplateIds: ["template-guest-standard", "template-conference-guest"],
    schedule: { mode: "always" },
    notes: "Standard guest areas stay open as long as card validity allows it.",
    status: "active",
  },
  {
    id: "rule-vip-default",
    roleId: "role-vip-guest",
    areaIds: ["area-gym"],
    zoneTemplateIds: ["template-vip-access", "template-conference-guest"],
    schedule: { mode: "always" },
    notes: "VIP guests inherit standard access plus lounge and spa.",
    status: "active",
  },
  {
    id: "rule-housekeeping-day",
    roleId: "role-housekeeping",
    areaIds: [],
    zoneTemplateIds: ["template-housekeeping-shift"],
    schedule: {
      mode: "windows",
      windows: [
        {
          id: "window-housekeeping-day",
          label: "08:00 - 18:00",
          startTime: "08:00",
          endTime: "18:00",
          weekdays: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
        },
      ],
    },
    notes: "Housekeeping cards are valid during operational cleaning windows.",
    status: "active",
  },
  {
    id: "rule-security-master",
    roleId: "role-security",
    areaIds: [],
    zoneTemplateIds: ["template-security-master"],
    schedule: { mode: "always" },
    notes: "Security operates 24/7 with broad coverage.",
    status: "active",
  },
  {
    id: "rule-maintenance-day",
    roleId: "role-maintenance",
    areaIds: [],
    zoneTemplateIds: ["template-maintenance-shift"],
    schedule: {
      mode: "windows",
      windows: [
        {
          id: "window-maintenance-day",
          label: "07:00 - 21:00",
          startTime: "07:00",
          endTime: "21:00",
          weekdays: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
        },
      ],
    },
    notes: "Maintenance cards are limited to daytime engineering work.",
    status: "active",
  },
  {
    id: "rule-manager",
    roleId: "role-manager",
    areaIds: ["area-spa", "area-conference"],
    zoneTemplateIds: ["template-guest-standard", "template-maintenance-shift"],
    schedule: {
      mode: "windows",
      windows: [
        {
          id: "window-manager-primary",
          label: "06:00 - 23:00",
          startTime: "06:00",
          endTime: "23:00",
          weekdays: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
        },
      ],
    },
    notes: "Managers can cross guest and service circulation with extended hours.",
    status: "active",
  },
  {
    id: "rule-front-office-supervisor",
    roleId: "role-front-office-supervisor",
    areaIds: ["area-room"],
    zoneTemplateIds: ["template-supervisor-ops"],
    schedule: {
      mode: "windows",
      windows: [
        {
          id: "window-front-office-supervisor",
          label: "06:00 - 23:00",
          startTime: "06:00",
          endTime: "23:00",
          weekdays: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
        },
      ],
    },
    notes: "Front office supervisors can move between front office, guest rooms and staff areas.",
    status: "active",
  },
  {
    id: "rule-it-admin",
    roleId: "role-it-admin",
    areaIds: [],
    zoneTemplateIds: ["template-it-admin"],
    schedule: { mode: "always" },
    notes: "IT admin access is available 24/7 for incident response and backend support.",
    status: "active",
  },
];

const CARD_ACCESS_PROFILES: CardAccessProfile[] = [
  {
    id: "profile-guest-standard",
    code: "guest_standard",
    name: "Guest Standard Profile",
    description: "Default profile for most stays.",
    roleId: "role-guest",
    areaIds: [],
    zoneTemplateIds: [],
    deniedAreaIds: [],
    status: "active",
  },
  {
    id: "profile-vip-signature",
    code: "vip_signature",
    name: "VIP Signature Profile",
    description: "VIP guest with premium amenities.",
    roleId: "role-vip-guest",
    areaIds: [],
    zoneTemplateIds: [],
    deniedAreaIds: [],
    status: "active",
  },
  {
    id: "profile-housekeeping-day",
    code: "housekeeping_day",
    name: "Housekeeping Day Shift",
    description: "Housekeeping shift profile with service-room access.",
    roleId: "role-housekeeping",
    areaIds: [],
    zoneTemplateIds: [],
    deniedAreaIds: [],
    status: "active",
  },
  {
    id: "profile-security-master",
    code: "security_master",
    name: "Security Master",
    description: "24/7 security coverage profile.",
    roleId: "role-security",
    areaIds: [],
    zoneTemplateIds: [],
    deniedAreaIds: [],
    status: "active",
  },
  {
    id: "profile-maintenance-day",
    code: "maintenance_day",
    name: "Maintenance Day Shift",
    description: "Maintenance with selected service and technical access.",
    roleId: "role-maintenance",
    areaIds: [],
    zoneTemplateIds: [],
    deniedAreaIds: [],
    status: "active",
  },
  {
    id: "profile-manager-ops",
    code: "manager_ops",
    name: "Manager Operations",
    description: "Operational management profile with extended schedule.",
    roleId: "role-manager",
    areaIds: [],
    zoneTemplateIds: [],
    deniedAreaIds: [],
    status: "active",
  },
  {
    id: "profile-front-office-supervisor",
    code: "front_office_supervisor",
    name: "Supervisor Access",
    description: "Front office supervision and staff circulation.",
    roleId: "role-front-office-supervisor",
    areaIds: [],
    zoneTemplateIds: [],
    deniedAreaIds: [],
    status: "active",
  },
  {
    id: "profile-it-admin",
    code: "it_admin",
    name: "IT Admin Access",
    description: "Technical and administrative access profile.",
    roleId: "role-it-admin",
    areaIds: [],
    zoneTemplateIds: [],
    deniedAreaIds: [],
    status: "active",
  },
];

export const DEFAULT_ACCESS_CONTROL_CATALOG: AccessControlCatalog = {
  roles: ACCESS_ROLES,
  areas: ACCESS_AREAS,
  zoneTemplates: ZONE_TEMPLATES,
  rules: ACCESS_RULES,
  cardAccessProfiles: CARD_ACCESS_PROFILES,
};
